import { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  addArticleToSupplier,
  addContactToSupplier,
  getSupplierById,
  editSupplier,
  deleteContact,
  deleteArticleSupplier,
  editContact,
  editArticleToSupplier,
  getAllBusinessUnits,
  getAllSuppliersPaymentMethods,
  getPurchaseUnitsFiltered,
  getArticlesFiltered,
  getAttributesFiltered,
  getSeasonsFiltered,
  getSupplierCataloguesBySupplierId,
  createSupplierCatalogue,
  editSupplierCatalogue,
  deleteSupplierCatalogue,
  deleteSupplierSeasonCatalogueFile,
} from "../../api/Services";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import { BusinessUniteContext } from "../../components/layout/Layout";
import {
  Form,
  message,
  Select,
  Modal,
  Button,
  Input,
  Switch,
  Spin,
  Table,
  Space,
} from "antd";
import ReusableUpload from "../../components/upload/ReusableUpload";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
  CheckOutlined,
    DownloadOutlined,
} from "@ant-design/icons";

import styles from "./ViewSupplier.module.scss";
import NotFound from "../notfound/NotFound";

const ViewSupplier = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    contextValue: businessUnitSelected,
    updateContextValue: setBusinessUnitSelected,
  } = useContext(BusinessUniteContext);

  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState<any>();
  const [isAddContactModalVisible, setIsAddContactModalVisible] =
    useState(false);
  const [isAddArticleModalVisible, setIsAddArticleModalVisible] =
    useState(false);
  const [articles, setArticles] = useState<any[]>([]);
  const [businessUnits, setBusinessUnits] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [purchaseUnits, setPurchaseUnits] = useState<any[]>([]);

  const [infoSupplierForm] = Form.useForm();
  const [addContactForm] = Form.useForm();
  const [addArticleForm] = Form.useForm();

  const [catalogues, setCatalogues] = useState<any[]>([]);
  const [loadingCatalogues, setLoadingCatalogues] = useState(false);
  const [isCatalogueModalVisible, setIsCatalogueModalVisible] = useState(false);
  const [editingCatalogue, setEditingCatalogue] = useState<any>(null);
  const [catalogueForm] = Form.useForm();
  const [attributes, setAttributes] = useState<any[]>([]);
  const [attributeFilterValue, setAttributeFilterValue] = useState("");
  const [seasons, setSeasons] = useState<any[]>([]);
  const [isAddPdfModalVisible, setIsAddPdfModalVisible] = useState(false);
  const [addPdfForm] = Form.useForm();
  const [isSeasonLinkModalVisible, setIsSeasonLinkModalVisible] =
    useState(false);
  const [editingSeasonLink, setEditingSeasonLink] = useState<any>(null);
  const [seasonLinkForm] = Form.useForm();

  const canEdit = hasPermission(privilegesEnum.WRITE_SUPPLIERS);

  const { id } = useParams();

  const openPdfInNewTab = async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const contentType = response.headers.get("Content-Type") || "application/pdf";
      const finalBlob = new Blob([blob], { type: contentType });
      const blobUrl = URL.createObjectURL(finalBlob);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_SUPPLIERS)) {
      handleFetchSupplier();
      handleFetchBusinessUnits();
      handleFetchPaymentMethods();
    }
  }, []);

  useEffect(() => {
    if (attributeFilterValue.length > 0) {
      handleFetchAttributes(attributeFilterValue);
    }
  }, [attributeFilterValue]);
  useEffect(() => {
    if (
      supplier &&
      !supplier.businessUnits
        .map((businessUnit: any) => businessUnit.id)
        .includes(businessUnitSelected) &&
      businessUnitSelected &&
      !loading
    ) {
      navigate("/admin/suppliers");
    }
  }, [businessUnitSelected]);

  const handleFetchSupplier = async () => {
    let response = await getSupplierById(id!);
    setSupplier(response);
    setArticles(
      response.articles?.map((articleTerm: any) => articleTerm.article)
    );
    setBusinessUnitSelected(response.businessUnits[0].id);

    const linkSeasons = (response.seasonCatalogueLinks || [])
      .map((scl: any) => ({ id: scl?.season?.id, name: scl?.season?.name }))
      .filter((s: any) => s.id && s.name);
    const fileSeasons = (response.seasonCatalogueFiles || [])
      .map((scf: any) => ({ id: scf?.season?.id, name: scf?.season?.name }))
      .filter((s: any) => s.id && s.name);
    const seasonMap = new Map<number, any>();
    [...linkSeasons, ...fileSeasons].forEach((s) => {
      if (s && s.id) seasonMap.set(s.id, s);
    });
    setSeasons(Array.from(seasonMap.values()));

    const existingPurchaseUnits = response.articles
      ?.map((article: any) => article.purchaseUnit)
      .filter((purchaseUnit: any) => purchaseUnit !== null);

    setPurchaseUnits(existingPurchaseUnits ?? []);
    setLoading(false);
  };

  const handleFetchBusinessUnits = async () => {
    const response = await getAllBusinessUnits();
    setBusinessUnits(response);
  };

  const handleSearchPurchaseUnits = async (value: string) => {
    if (value.length > 2) {
      const response = await getPurchaseUnitsFiltered(value);
      setPurchaseUnits(response);
    } else setPurchaseUnits([]);
  };

  const handleFetchPaymentMethods = async () => {
    const response = await getAllSuppliersPaymentMethods();
    setPaymentMethods(response);
  };

  const handleFetchArticles = async (nameFilterValue: string) => {
    const response = await getArticlesFiltered(0, {
      articleName: nameFilterValue,
    });
    setArticles(response.articles);
  };

  const handleFetchAttributes = async (name: string) => {
    const response = await getAttributesFiltered(name);
    setAttributes(response);
  };

  // Season catalogue links helpers
  const openAddSeasonLinkModal = () => {
    setEditingSeasonLink(null);
    seasonLinkForm.resetFields();
    setIsSeasonLinkModalVisible(true);
  };

  const openEditSeasonLinkModal = (record: any) => {
    setEditingSeasonLink(record);
    seasonLinkForm.setFieldsValue({
      seasonId: record?.season?.id,
      catalogueLink: record?.link,
    });
    setIsSeasonLinkModalVisible(true);
  };

  const cancelSeasonLinkModal = () => {
    setIsSeasonLinkModalVisible(false);
    setEditingSeasonLink(null);
  };

  const updateSeasonLinks = async (
    payloadLinks: Array<{ seasonId: number; catalogueLink: string }>
  ) => {
    setLoading(true);
    const paymentMethodsIds =
      supplier?.paymentMethods?.map((p: any) => p.id) ?? [];
    const supplierPayload = {
      name: supplier?.name,
      externalId: supplier?.externalId,
      website: supplier?.website,
      seasonCatalogueLinks: payloadLinks,
      businessUnitsIds: [businessUnitSelected],
      supplierPaymentMethodsIds: paymentMethodsIds,
    };
    const ok = await editSupplier(id!, supplierPayload);
    if (ok) {
      message.success(t("Common-EditSuccessMessage"));
      await handleFetchSupplier();
    } else {
      message.error(t("Common-EditErrorMessage"));
    }
    setLoading(false);
  };

  const saveSeasonLink = async (values: any) => {
    const currentPayload = (supplier?.seasonCatalogueLinks || []).map(
      (l: any) => ({
        seasonId: l?.season?.id,
        catalogueLink: l?.link,
      })
    );
    let newPayload = currentPayload;
    if (editingSeasonLink) {
      newPayload = currentPayload.filter(
        (l: any) => l.seasonId !== editingSeasonLink?.season?.id
      );
      newPayload.push({
        seasonId: values.seasonId,
        catalogueLink: values.catalogueLink,
      });
    } else {
      newPayload = [
        ...currentPayload,
        { seasonId: values.seasonId, catalogueLink: values.catalogueLink },
      ];
    }
    await updateSeasonLinks(newPayload);
    cancelSeasonLinkModal();
  };

  const deleteSeasonLink = async (record: any) => {
    const currentPayload = (supplier?.seasonCatalogueLinks || []).map(
      (l: any) => ({
        seasonId: l?.season?.id,
        catalogueLink: l?.link,
      })
    );
    const newPayload = currentPayload.filter(
      (l: any) => l.seasonId !== record?.season?.id
    );
    await updateSeasonLinks(newPayload);
  };

  const attributeOptions = () => {
    return attributes?.map((attribute) => {
      return {
        disabled: attribute.enums.length === 0,
        value: attribute.id,
        label: attribute.attribute,
        children: attribute.enums?.map((possibleValue: any) => {
          return {
            value: possibleValue.id,
            label: possibleValue.value,
          };
        }),
      };
    });
  };
  const handleCancelButton = () => {
    infoSupplierForm.resetFields();
  };

  const handleEditSupplier = async () => {
    setLoading(true);

    const {
      name,
      website,
      externalId,
      seasonCatalogueLinks,
      supplierPaymentMethodsIds,
      seasonCatalogueFiles,
    } = infoSupplierForm.getFieldsValue();

    const supplierPayload = {
      name,
      externalId,
      website,
      seasonCatalogueLinks: seasonCatalogueLinks ?? [],
      businessUnitsIds: [businessUnitSelected],
      supplierPaymentMethodsIds: supplierPaymentMethodsIds ?? [],
    };

    const fileItems: any[] = seasonCatalogueFiles ?? [];
    const seasonFileSeasonIds: number[] = [];
    const seasonFiles: File[] = [];
    if (Array.isArray(fileItems)) {
      fileItems.forEach((it: any) => {
        const sid = it?.seasonId;
        const f = it?.file?.[0]?.originFileObj as File | undefined;
        if (sid && f) {
          seasonFileSeasonIds.push(sid);
          seasonFiles.push(f);
        }
      });
    }

    const response = await editSupplier(
      id!,
      supplierPayload,
      seasonFileSeasonIds.length > 0 ? seasonFileSeasonIds : null,
      seasonFiles.length > 0 ? seasonFiles : null
    );

    if (response) message.success(t("Common-EditSuccessMessage"));
    else message.error(t("Common-EditErrorMessage"));
    setLoading(false);
  };

  const getArticlesOptions = () => {
    const options: any[] = [];
    const suppliersArticles = supplier.articles;

    articles?.forEach(function (item: any) {
      const disabled = suppliersArticles.find(
        (s: any) => s.article.id === item.id
      );
      options.push(
        <Select.Option value={item.id} key={item.id} disabled={disabled}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const getArticlesOptionsEditing = (articleEditingId: number) => {
    const options: any[] = [];
    const suppliersArticles = supplier.articles;

    articles?.forEach(function (item: any) {
      const disabled = suppliersArticles.find(
        (s: any) =>
          s.article.id === item.id && s.article.id !== articleEditingId
      );
      options.push(
        <Select.Option value={item.id} key={item.id} disabled={disabled}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const handleOpenAddContactModal = () => {
    setIsAddContactModalVisible(true);
  };

  const handleCancelAddContactModal = () => {
    setIsAddContactModalVisible(false);
  };

  const handleOpenAddArticleModal = () => {
    setIsAddArticleModalVisible(true);
  };

  const handleCancelAddArticleModal = () => {
    setIsAddArticleModalVisible(false);
  };

  const handleAddContact = async () => {
    setLoading(true);

    const contact = addContactForm.getFieldsValue();

    const { name, role, phoneNumber, email } = contact;

    const response = await addContactToSupplier(
      parseInt(id!),
      name,
      role,
      phoneNumber,
      email
    );

    if (response) {
      addContactForm.resetFields();
      await handleFetchSupplier();
      handleCancelAddContactModal();
      message.success(t("Common-AddSuccessMessage"));
    } else message.error(t("Common-ErrorMessage"));
    setLoading(false);
  };

  const handleAddArticle = async () => {
    setLoading(true);

    const article = addArticleForm.getFieldsValue();

    const {
      articleId,
      supplierPaymentMethodId,
      deliveryTerm,
      minimumOrder,
      purchaseUnit,
      cost,
      agreedDiscounts,
      principal,
    } = article;

    const commercialTerm = {
      deliveryTerm: deliveryTerm,
      minimumOrder: minimumOrder,
      cost: parseInt(cost),
      agreedDiscounts,
      supplierPaymentMethodId,
    };

    const response = await addArticleToSupplier(
      articleId,
      principal,
      parseInt(id!),
      purchaseUnit,
      commercialTerm
    );

    if (response) {
      addArticleForm.resetFields();
      await handleFetchSupplier();
      handleCancelAddArticleModal();
      message.success(t("Common-AddSuccessMessage"));
    } else message.error(t("Common-ErrorMessage"));
    setLoading(false);
  };

  const contactsColumns = [
    {
      title: t("Common-Name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("Common-Rol"),
      dataIndex: "role",
      key: "role",
    },
    {
      title: t("NewSupplier-ContactNumber"),
      dataIndex: "phoneNumber",
      key: "phoneNumber",
    },
    {
      title: t("NewSupplier-ContactEmail"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <Space size="middle">
          <EditOutlined
            onClick={() => {
              showEditContactModal(record);
            }}
          />
          <DeleteOutlined
            onClick={() => {
              showDeleteContactConfirm(record.id);
            }}
          />
        </Space>
      ),
      hidden: !canEdit,
    },
  ].filter((item) => !item.hidden);

  const showEditContactModal = (contact: any) => {
    Modal.info({
      title: t("EditSupplier-Contact"),
      closable: true,
      maskClosable: true,
      okButtonProps: { style: { display: "none" } },
      width: 500,
      icon: null,
      content: (
        <Form
          name="editContact"
          initialValues={{ remember: true }}
          autoComplete="off"
          onFinish={handleEditContact}
          preserve={false}
        >
          <Form.Item hidden initialValue={contact.id} name="id" />
          <Form.Item
            className={styles.contactItem}
            label={t("NewSupplier-ContactName")}
            name="name"
            rules={[
              {
                required: true,
                message: t("NewSupplier-ContactNameNotEntered"),
              },
            ]}
            initialValue={contact.name}
          >
            <Input placeholder={t("NewSupplier-ContactName")} />
          </Form.Item>
          <Form.Item
            className={styles.contactItem}
            label={t("Common-Rol")}
            name="role"
            initialValue={contact.role}
          >
            <Input placeholder={t("Common-Rol")} />
          </Form.Item>
          <Form.Item
            className={styles.contactItem}
            label={t("NewSupplier-ContactNumber")}
            name="phoneNumber"
            initialValue={contact.phoneNumber}
          >
            <Input placeholder={t("NewSupplier-ContactNumber")} />
          </Form.Item>
          <Form.Item
            className={styles.contactItem}
            label={t("NewSupplier-ContactEmail")}
            name="email"
            rules={[
              {
                type: "email",
                message: t("NewSupplier-ContactEmailNotEntered"),
              },
            ]}
            initialValue={contact.email}
          >
            <Input placeholder={t("NewSupplier-ContactEmailNotEntered")} />
          </Form.Item>
          <Button
            className={styles.addContactButton}
            type="primary"
            htmlType="submit"
          >
            {t("Common-Save")}
          </Button>
        </Form>
      ),
    });
  };

  const getOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const handleEditContact = async (data: any) => {
    setLoading(true);

    const { id, role, name, email, phoneNumber } = data;

    let response = await editContact(id, name, role, email, phoneNumber);

    if (response) {
      message.success(t("Common-EditSuccessMessage"));
    } else message.error(t("Common-EditErrorMessage"));

    Modal.destroyAll();
    await handleFetchSupplier();
    setLoading(false);
  };

  const showDeleteContactConfirm = (contactId: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDeleteContact(contactId);
      },
    });
  };

  const getOptionsPurchaseUnit = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.purchaseUnit}
        </Select.Option>
      );
    });

    return options;
  };

  const handleDeleteContact = async (contactId: number) => {
    setLoading(true);
    const deleteStatus = await deleteContact(contactId);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await handleFetchSupplier();
    setLoading(false);
  };

  const articleColumn = [
    {
      title: t("Common-Name"),
      dataIndex: "article",
      key: "article",
      render: (article: any) => article?.name,
    },
    {
      title: t("NewSupplier-DeliveryTerm"),
      dataIndex: "commercialTerm",
      key: "deliveryTerm",
      render: (commercialTerm: any) => commercialTerm?.deliveryTerm,
    },
    {
      title: t("NewSupplier-MinimumOrder"),
      dataIndex: "commercialTerm",
      key: "minimumOrder",
      render: (commercialTerm: any) => commercialTerm?.minimumOrder,
    },
    {
      title: t("NewSupplier-PurchaseUnit"),
      dataIndex: "purchaseUnit",
      key: "purchaseUnit",
      render: (purchaseUnit: any) => purchaseUnit?.purchaseUnit,
    },
    {
      title: t("Common-Cost"),
      dataIndex: "commercialTerm",
      key: "cost",
      render: (commercialTerm: any) => commercialTerm?.cost,
    },
    {
      title: t("Common-AgreedDiscounts"),
      dataIndex: "commercialTerm",
      key: "agreedDiscounts",
      render: (commercialTerm: any) => commercialTerm?.agreedDiscounts,
    },
    {
      title: t("NewSupplier-Principal"),
      dataIndex: "principal",
      key: "principal",
      render: (principal: boolean) => <>{principal && <CheckOutlined />}</>,
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <Space size="middle">
          <EditOutlined
            onClick={() => {
              showEditArticleModal(record);
            }}
          />
          <DeleteOutlined
            onClick={() => {
              showDeleteArticleConfirm(record.id);
            }}
          />
        </Space>
      ),
      hidden: !canEdit,
    },
  ].filter((item) => !item.hidden);

  const showEditArticleModal = (article: any) => {
    Modal.info({
      title: t("Articles-EditArticle"),
      closable: true,
      maskClosable: true,
      okButtonProps: { style: { display: "none" } },
      width: 500,
      icon: null,
      content: (
        <Form
          name="editArticle"
          initialValues={{ remember: true }}
          autoComplete="off"
          onFinish={handleEditArticle}
          preserve={false}
        >
          <Form.Item hidden initialValue={article.id} name="id" />
          <Form.Item
            className={styles.articleInfoItem}
            name="articleId"
            label={t("Common-Article")}
            rules={[
              {
                required: true,
                message: t("Common-Article"),
              },
            ]}
            initialValue={article.article.id}
          >
            <Select placeholder={t("Common-Article")}>
              {getArticlesOptionsEditing(article.article.id)}
            </Select>
          </Form.Item>
          <Form.Item
            className={styles.articleInfoItem}
            label={t("Common-PaymentMethod")}
            name="supplierPaymentMethodId"
            initialValue={article.commercialTerm.supplierPaymentMethod?.id}
          >
            <Select
              allowClear
              placeholder={t("Common-PaymentMethod")}
              showSearch
              optionFilterProp="children"
              filterOption={(input: string, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {getOptions(supplier?.paymentMethods)}
            </Select>
          </Form.Item>
          <Form.Item
            className={styles.articleInfoItem}
            name="deliveryTerm"
            label={t("NewSupplier-DeliveryTerm")}
            initialValue={article.commercialTerm.deliveryTerm}
            rules={[
              {
                required: true,
                message: t("Common-DeliveryTerm"),
              },
            ]}
          >
            <Input placeholder={t("NewSupplier-DeliveryTerm")} />
          </Form.Item>
          <Form.Item
            className={styles.articleInfoItem}
            name="minimumOrder"
            label={t("NewSupplier-MinimumOrder")}
            rules={[
              {
                required: true,
                message: t("NewSupplier-MinimumOrder"),
              },
            ]}
            initialValue={article.commercialTerm.minimumOrder}
          >
            <Input placeholder={t("NewSupplier-MinimumOrder")} />
          </Form.Item>
          <Form.Item
            className={styles.articleInfoItem}
            name="purchaseUnit"
            label={t("NewSupplier-PurchaseUnit")}
            initialValue={article.purchaseUnit?.id}
          >
            <Select
              allowClear
              placeholder={t("Common-SearchByName")}
              showSearch
              onSearch={handleSearchPurchaseUnits}
              optionFilterProp="children"
              filterOption={(input: string, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {getOptionsPurchaseUnit(purchaseUnits)}
            </Select>
          </Form.Item>
          <Form.Item
            label={t("Common-Cost")}
            className={styles.articleInfoItem}
            name={"cost"}
            rules={[
              {
                required: true,
                message: t("Common-Cost"),
              },
            ]}
            initialValue={article.commercialTerm.cost}
          >
            <Input
              className={styles.articleInfoItem}
              placeholder={t("Common-Cost")}
            />
          </Form.Item>
          <Form.Item
            label={t("Common-AgreedDiscounts")}
            className={styles.articleInfoItem}
            name={"agreedDiscounts"}
            rules={[
              {
                required: true,
                message: t("Common-AgreedDiscounts"),
              },
            ]}
            initialValue={article.commercialTerm.agreedDiscounts}
          >
            <Input
              className={styles.articleInfoItem}
              placeholder={t("Common-AgreedDiscounts")}
            />
          </Form.Item>
          <Form.Item
            initialValue={article.principal}
            label={t("NewSupplier-Principal")}
            name="principal"
          >
            <Switch defaultChecked={article.principal} />
          </Form.Item>
          <Button
            className={styles.addArticleButton}
            type="primary"
            htmlType="submit"
          >
            {t("Common-Save")}
          </Button>
        </Form>
      ),
    });
  };

  const handleEditArticle = async (article: any) => {
    setLoading(true);

    const {
      id,
      articleId,
      deliveryTerm,
      minimumOrder,
      purchaseUnit,
      cost,
      agreedDiscounts,
      principal,
      supplierPaymentMethodId,
    } = article;

    const commercialTerm = {
      deliveryTerm: deliveryTerm,
      minimumOrder: minimumOrder,
      purchaseUnit: purchaseUnit,
      agreedDiscounts,
      cost,
      supplierPaymentMethodId,
    };

    let response = await editArticleToSupplier(
      id,
      articleId,
      principal,
      purchaseUnit,
      commercialTerm
    );

    if (response) {
      message.success(t("Common-EditSuccessMessage"));
    } else message.error(t("Common-EditErrorMessage"));

    Modal.destroyAll();
    await handleFetchSupplier();
    setLoading(false);
  };

  const showDeleteArticleConfirm = (articleId: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDeleteArticle(articleId);
      },
    });
  };

  const handleDeleteArticle = async (articleId: number) => {
    setLoading(true);
    const deleteStatus = await deleteArticleSupplier(articleId);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await handleFetchSupplier();
    setLoading(false);
  };

  const fetchCatalogues = async () => {
    if (!supplier?.id) return;
    setLoadingCatalogues(true);
    try {
      const data = await getSupplierCataloguesBySupplierId(supplier.id);
      setCatalogues(data || []);
    } catch (error) {
      setCatalogues([]);
    } finally {
      setLoadingCatalogues(false);
    }
  };

  useEffect(() => {
    if (supplier?.id) {
      fetchCatalogues();
    }
  }, [supplier]);

  const handleOpenAddCatalogueModal = () => {
    setEditingCatalogue(null);
    catalogueForm.resetFields();
    setIsCatalogueModalVisible(true);
  };

  const handleOpenEditCatalogueModal = (record: any) => {
    setEditingCatalogue(record);
    catalogueForm.setFieldsValue(record);
    setIsCatalogueModalVisible(true);
  };

  const handleCancelCatalogueModal = () => {
    setIsCatalogueModalVisible(false);
    setEditingCatalogue(null);
  };

  const handleDeleteCatalogue = async (id: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        await deleteSupplierCatalogue(id);
        message.success(t("Common-DeleteSuccessMessage"));
        fetchCatalogues();
      },
    });
  };

  const handleSaveCatalogue = async (values: any) => {
    try {
      if (editingCatalogue) {
        await editSupplierCatalogue(editingCatalogue.id, {
          ...values,
          supplierId: supplier.id,
        });
        message.success(t("Common-EditSuccessMessage"));
      } else {
        await createSupplierCatalogue({
          ...values,
          supplierId: supplier.id,
        });
        message.success(t("Common-AddSuccessMessage"));
      }
      handleCancelCatalogueModal();
      fetchCatalogues();
    } catch (error: any) {}
  };

  const catalogueColumns = [
    { title: t("Common-Name"), dataIndex: "name" },
    { title: t("Common-Brand"), dataIndex: "brand" },
    { title: t("Common-ArticleCode"), dataIndex: "articleCode" },
    { title: t("Common-Description"), dataIndex: "description" },
    {
      title: t("Common-Photo"),
      dataIndex: "photo",
      render: (url: string) =>
        url && <img src={url} style={{ width: 40 }} alt="foto" />,
    },
    { title: t("Common-Size"), dataIndex: "size" },
    { title: t("Common-Colours"), dataIndex: "colours" },
    { title: t("Common-Material"), dataIndex: "material" },
    { title: t("Common-Cost"), dataIndex: "cost" },
    {
      title: t("Common-Attributes"),
      dataIndex: "attributes",
      render: (attrs: any) => attrs?.map((a: any) => a?.attribute).join(", "),
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (_: any, record: any) => (
        <Space size="middle">
          <EditOutlined
            style={{ cursor: "pointer" }}
            onClick={() => handleOpenEditCatalogueModal(record)}
          />
          <DeleteOutlined
            style={{ cursor: "pointer" }}
            onClick={() => handleDeleteCatalogue(record.id)}
          />
        </Space>
      ),
      hidden: !canEdit,
    },
  ];

  return loading ? (
    <Spin
      indicator={<LoadingOutlined className="ol-loading-spin-icon" />}
      delay={500}
    />
  ) : !supplier ? (
    <NotFound />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("Common-Supplier")}</h1>
        {hasPermission(privilegesEnum.WRITE_SUPPLIERS) && (
          <div className={styles.actionButtonsWrapper}>
            <Button
              onClick={handleEditSupplier}
              className={styles.saveButton}
              type="primary"
            >
              {t("Common-Save")}
            </Button>
            <Button
              onClick={handleCancelButton}
              className={styles.cancelButton}
              type="primary"
            >
              {t("Common-Cancel")}
            </Button>
          </div>
        )}
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.supplierInfoWrapper}>
          <Form
            name="basic"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 12 }}
            initialValues={{ remember: true }}
            autoComplete="off"
            className={styles.newForm}
            form={infoSupplierForm}
          >
            <Form.Item
              label={t("Common-Name")}
              name="name"
              initialValue={supplier?.name}
              rules={[
                {
                  required: true,
                  message: t("Common-Name"),
                },
              ]}
            >
              <Input placeholder={t("Common-Name")} disabled={!canEdit} />
            </Form.Item>
            <Form.Item
              label={t("Common-ExternalId")}
              name="externalId"
              initialValue={supplier?.externalId}
            >
              <Input disabled={!canEdit} placeholder={t("Common-ExternalId")} />
            </Form.Item>

            <Form.Item
              label={t("Common-AcceptedPaymentMethods")}
              name="supplierPaymentMethodsIds"
              className={styles.editInfoItem}
              initialValue={supplier?.paymentMethods?.map((pa: any) => pa.id)}
            >
              <Select
                allowClear
                placeholder={t("Common-AcceptedPaymentMethods")}
                showSearch
                mode="multiple"
                optionFilterProp="children"
                defaultValue={supplier?.paymentMethods?.map((pa: any) => pa.id)}
                filterOption={(input: string, option: any) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {getOptions(paymentMethods)}
              </Select>
            </Form.Item>
            <Form.Item
              label={t("Common-Website")}
              name="website"
              initialValue={supplier?.website}
              rules={[
                {
                  type: "url",
                  message: t("Common-Website"),
                },
              ]}
            >
              <Input placeholder={t("Common-exampleURL")} disabled={!canEdit} />
            </Form.Item>
          </Form>
        </div>
        {/* Catalogue links section as table */}
        <div className={styles.contactWrapper}>
          <div className={styles.tablesHeader}>
            <h1 className={styles.contactsTitle}>
              {t("Suppliers-CatalogueLink")}
            </h1>
            {canEdit && (
              <Button
                type="primary"
                className={styles.actionsButton}
                onClick={openAddSeasonLinkModal}
                icon={<PlusOutlined />}
              />
            )}
          </div>
          <div className={styles.contactsTableWrapper}>
            <Table
              size="small"
              rowKey={(record) => record.id}
              className={styles.contactTable}
              loading={loading}
              columns={[
                {
                  title: t("Common-Season"),
                  key: "season",
                  render: (_: any, r: any) => r?.season?.name,
                },
                {
                  title: t("Suppliers-CatalogueLink"),
                  key: "link",
                  render: (_: any, r: any) => (
                    <a href={r?.link} target="_blank" rel="noreferrer">
                      {r?.link}
                    </a>
                  ),
                },
                {
                  title: t("Common-Actions"),
                  key: "action",
                  render: (_: any, record: any) => (
                    <Space size="middle">
                      <EditOutlined
                        style={{ cursor: "pointer" }}
                        onClick={() => openEditSeasonLinkModal(record)}
                      />
                      <DeleteOutlined
                        style={{ cursor: "pointer" }}
                        onClick={() => deleteSeasonLink(record)}
                      />
                    </Space>
                  ),
                  hidden: !canEdit,
                },
              ].filter((c: any) => !c.hidden)}
              dataSource={supplier?.seasonCatalogueLinks}
            />
          </div>
        </div>
        {/* Season PDF files table + uploader */}
        <div className={styles.contactWrapper}>
          <div className={styles.tablesHeader}>
            <h1 className={styles.contactsTitle}>
              {t("Suppliers-CataloguePdf")}
            </h1>
            {canEdit && (
              <Button
                type="primary"
                className={styles.actionsButton}
                onClick={() => {
                  addPdfForm.resetFields();
                  setIsAddPdfModalVisible(true);
                }}
                icon={<PlusOutlined />}
              />
            )}
          </div>
          <div className={styles.contactsTableWrapper}>
            <Form form={infoSupplierForm} layout="vertical">
              {/* Existing season PDFs list */}
              <Table
                size="small"
                rowKey={(record: any) => `existing-${record.id}`}
                className={styles.contactTable}
                loading={loading}
                columns={[
                  {
                    title: t("Common-Season"),
                    key: "season",
                    render: (_: any, r: any) => r?.season?.name,
                  },
                  {
                    title: "PDF",
                    key: "pdf",
                  render: (_: any, r: any) =>
                      r?.file?.downloadUrl ? (
                        <Button
                          type="link"
                          onClick={() => openPdfInNewTab(r.file.downloadUrl)}
                          style={{ padding: 0 }}
                        >
                          {r.file.originalFilename || t("Common-Download")}
                        </Button>
                      ) : (
                        "-"
                      ),
                  },
                  {
                    title: t("Common-Actions"),
                    key: "action",
                    render: (_: any, r: any) => (
                      <Space size="middle">
                        {r?.file?.downloadUrl && (
                          <a href={r.file.downloadUrl} download>
                            <DownloadOutlined />
                          </a>
                        )}
                        {canEdit && (
                          <DeleteOutlined
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              Modal.confirm({
                                title: t("Common-DeleteConfirm"),
                                icon: <ExclamationCircleOutlined />,
                                onOk: async () => {
                                  setLoading(true);
                                  const ok = await deleteSupplierSeasonCatalogueFile(
                                    id!,
                                    r?.season?.id
                                  );
                                  if (ok) {
                                    message.success(t("Common-DeleteSuccessMessage"));
                                    await handleFetchSupplier();
                                  } else {
                                    message.error(t("Common-DeleteErrorMessage"));
                                  }
                                  setLoading(false);
                                },
                              });
                            }}
                          />
                        )}
                      </Space>
                    ),
                  },
                ]}
                dataSource={supplier?.seasonCatalogueFiles}
                pagination={false}
              />
              {/* creation moved to modal */}
            </Form>
          </div>
        </div>

        <Modal
          title={t("Suppliers-CataloguePdf")}
          visible={isAddPdfModalVisible}
          onCancel={() => setIsAddPdfModalVisible(false)}
          footer={null}
          destroyOnClose
        >
          <Form
            form={addPdfForm}
            layout="vertical"
            onFinish={async (values: any) => {
              const { seasonId, file } = values;
              if (!seasonId || !file?.[0]?.originFileObj) return;
              setLoading(true);
              const supplierPayload = {
                name: supplier?.name,
                externalId: supplier?.externalId,
                website: supplier?.website,
                seasonCatalogueLinks: supplier?.seasonCatalogueLinks || [],
                businessUnitsIds: [businessUnitSelected],
                supplierPaymentMethodsIds:
                  supplier?.paymentMethods?.map((p: any) => p.id) || [],
              };
              const ok = await editSupplier(
                id!,
                supplierPayload,
                [seasonId],
                [file[0].originFileObj as File]
              );
              if (ok) {
                message.success(t("Common-AddSuccessMessage"));
                setIsAddPdfModalVisible(false);
                await handleFetchSupplier();
              } else {
                message.error(t("Common-ErrorMessage"));
              }
              setLoading(false);
            }}
          >
            <Form.Item
              name="seasonId"
              label={t("Common-Season")}
              rules={[{ required: true, message: t("Common-Season") }]}
            >
              <Select
                allowClear
                placeholder={t("Common-SearchByName")}
                showSearch
                filterOption={(input: string, option: any) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                onSearch={async (value) => {
                  if (value.length > 1) {
                    const res = await getSeasonsFiltered(value);
                    setSeasons(res);
                  } else setSeasons([]);
                }}
              >
                {seasons?.map((s: any) => (
                  <Select.Option key={s.id} value={s.id}>
                    {s.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item shouldUpdate noStyle>
              {() => (
                <Form.Item
                  name="file"
                  label="PDF"
                  rules={[{ required: true, message: "PDF" }]}
                  valuePropName="fileList"
                  getValueFromEvent={(e: any) => e?.fileList}
                >
                  <ReusableUpload
                    accept="application/pdf"
                    minimal
                    fullWidth
                    fileList={addPdfForm.getFieldValue("file") || []}
                    onChange={({ fileList }) => addPdfForm.setFieldsValue({ file: fileList })}
                  />
                </Form.Item>
              )}
            </Form.Item>

            <Button type="primary" htmlType="submit" style={{ width: "100%" }}>
              {t("Common-Save")}
            </Button>
          </Form>
        </Modal>
        <div className={styles.contactWrapper}>
          <div className={styles.tablesHeader}>
            <h1 className={styles.contactsTitle}>
              {t("NewSupplier-Contacts")}
            </h1>
            {canEdit && (
              <Button
                type="primary"
                className={styles.actionsButton}
                onClick={() => handleOpenAddContactModal()}
                icon={<PlusOutlined />}
              />
            )}
          </div>
          <div className={styles.contactsTableWrapper}>
            <Table
              size="small"
              rowKey={(record) => record.id}
              className={styles.contactTable}
              loading={loading}
              columns={contactsColumns}
              dataSource={supplier?.contacts}
            />
          </div>
        </div>
        <div className={styles.articlesWrapper}>
          <div className={styles.tablesHeader}>
            <h1 className={styles.articlesTitle}>{t("Common-Articles")}</h1>
            {canEdit && (
              <Button
                type="primary"
                className={styles.actionsButton}
                onClick={() => handleOpenAddArticleModal()}
                icon={<PlusOutlined />}
              />
            )}
          </div>
          <div className={styles.articlesTableWrapper}>
            <Table
              size="small"
              rowKey={(record) => record.id}
              className={styles.articleTable}
              loading={loading}
              columns={articleColumn}
              dataSource={supplier?.articles}
            />
          </div>
        </div>
        <div className={styles.articlesWrapper}>
          <div className={styles.tablesHeader}>
            <h1 className={styles.articlesTitle}>
              {t("Common-CatalogueSupplier")}
            </h1>
            {canEdit && (
              <Button
                type="primary"
                className={styles.actionsButton}
                onClick={handleOpenAddCatalogueModal}
                icon={<PlusOutlined />}
              />
            )}
          </div>
          <div className={styles.articlesTableWrapper}>
            <Table
              size="small"
              rowKey={(record) => record.id}
              className={styles.articleTable}
              loading={loadingCatalogues}
              columns={catalogueColumns}
              dataSource={catalogues}
            />
          </div>
        </div>
        <Modal
          title={editingSeasonLink ? t("Common-Edit") : t("Common-Add")}
          visible={isSeasonLinkModalVisible}
          onCancel={cancelSeasonLinkModal}
          footer={null}
        >
          <Form
            form={seasonLinkForm}
            layout="vertical"
            onFinish={saveSeasonLink}
          >
            <Form.Item
              name="seasonId"
              label={t("Common-Season")}
              rules={[{ required: true, message: t("Common-Season") }]}
            >
              <Select
                allowClear
                placeholder={t("Common-SearchByName")}
                showSearch
                filterOption={(input: string, option: any) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                onSearch={async (value) => {
                  if (value.length > 1) {
                    const res = await getSeasonsFiltered(value);
                    setSeasons(res);
                  } else setSeasons([]);
                }}
              >
                {seasons?.map((s: any) => (
                  <Select.Option key={s.id} value={s.id}>
                    {s.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="catalogueLink"
              label={t("Suppliers-CatalogueLink")}
              rules={[{ type: "url", message: t("Common-UrlNotEntered") }]}
            >
              <Input placeholder={t("Common-exampleURL")} />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "100%" }}
              >
                {editingSeasonLink ? t("Common-Save") : t("Common-Add")}
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>
      <Modal
        title={editingCatalogue ? t("Common-Edit") : t("Common-Add")}
        visible={isCatalogueModalVisible}
        onCancel={handleCancelCatalogueModal}
        footer={null}
      >
        <Form
          form={catalogueForm}
          layout="vertical"
          onFinish={handleSaveCatalogue}
        >
          <Form.Item
            name="name"
            label={t("Common-Name")}
            rules={[{ required: true, message: t("Common-NameNotEntered") }]}
          >
            <Input placeholder={t("Common-Name")} />
          </Form.Item>
          <Form.Item name="brand" label={t("Common-Brand")}>
            <Input placeholder={t("Common-Brand")} />
          </Form.Item>
          <Form.Item name="articleCode" label={t("Common-ArticleCode")}>
            <Input placeholder={t("Common-ArticleCode")} />
          </Form.Item>
          <Form.Item name="description" label={t("Common-Description")}>
            <Input.TextArea placeholder={t("Common-Description")} rows={3} />
          </Form.Item>
          <Form.Item name="photo" label={t("Common-Photo")}>
            <Input placeholder={t("Common-Photo")} />
          </Form.Item>
          <Form.Item name="size" label={t("Common-Size")}>
            <Input placeholder={t("Common-Size")} />
          </Form.Item>
          <Form.Item name="colours" label={t("Common-Colours")}>
            <Input placeholder={t("Common-Colours")} />
          </Form.Item>
          <Form.Item name="material" label={t("Common-Material")}>
            <Input placeholder={t("Common-Material")} />
          </Form.Item>
          <Form.Item name="cost" label={t("Common-Cost")}>
            <Input type="number" placeholder={t("Common-Cost")} />
          </Form.Item>
          <Form.Item name="attributesIds" label={t("Common-Attributes")}>
            <Select
              showSearch
              onSearch={(value) => setAttributeFilterValue(value)}
              placeholder={t("Common-Attributes")}
              mode="multiple"
              allowClear
              optionFilterProp="label"
            >
              {attributes?.map((attribute) => (
                <Select.Option
                  key={attribute.id}
                  value={attribute.id}
                  label={attribute.attribute}
                >
                  {attribute.attribute}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ width: "100%" }}>
              {editingCatalogue ? t("Common-Save") : t("Common-Add")}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={t("Common-Contact")}
        visible={isAddContactModalVisible}
        okButtonProps={{ style: { display: "none" } }}
        cancelButtonProps={{ style: { display: "none" } }}
        onCancel={handleCancelAddContactModal}
      >
        <Form
          name="addContact"
          initialValues={{ remember: true }}
          autoComplete="off"
          form={addContactForm}
        >
          <Form.Item
            className={styles.contactItem}
            label={t("NewSupplier-ContactName")}
            name="name"
            rules={[
              {
                required: true,
                message: t("NewSupplier-ContactNameNotEntered"),
              },
            ]}
          >
            <Input placeholder={t("NewSupplier-ContactName")} />
          </Form.Item>
          <Form.Item
            className={styles.contactItem}
            label={t("Common-Rol")}
            name="role"
          >
            <Input placeholder={t("Common-Rol")} />
          </Form.Item>
          <Form.Item
            className={styles.contactItem}
            label={t("NewSupplier-ContactNumber")}
            name="phoneNumber"
          >
            <Input placeholder={t("NewSupplier-ContactNumber")} />
          </Form.Item>
          <Form.Item
            className={styles.contactItem}
            label={t("NewSupplier-ContactEmail")}
            name="email"
            rules={[
              {
                type: "email",
                message: t("NewSupplier-ContactEmailNotEntered"),
              },
            ]}
          >
            <Input placeholder={t("NewSupplier-ContactEmailNotEntered")} />
          </Form.Item>
        </Form>
        <Button
          className={styles.addContactButton}
          type="primary"
          onClick={handleAddContact}
        >
          {t("Common-Add")}
        </Button>
      </Modal>
      <Modal
        title={t("Common-Article")}
        visible={isAddArticleModalVisible}
        okButtonProps={{ style: { display: "none" } }}
        cancelButtonProps={{ style: { display: "none" } }}
        onCancel={handleCancelAddArticleModal}
      >
        <Form
          name="addArticle"
          initialValues={{ remember: true }}
          autoComplete="off"
          form={addArticleForm}
        >
          <Form.Item
            className={styles.articleInfoItem}
            name="articleId"
            label={t("Common-Article")}
            rules={[
              {
                required: true,
                message: t("Common-Article"),
              },
            ]}
          >
            <Select
              className={styles.articleInfoItem}
              allowClear
              placeholder={t("Common-SearchByName")}
              filterOption={(input: string, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              showSearch
              style={{ width: "100%" }}
              onSearch={(value) => handleFetchArticles(value)}
            >
              {getArticlesOptions()}
            </Select>
          </Form.Item>
          <Form.Item
            className={styles.articleInfoItem}
            label={t("Common-PaymentMethod")}
            name="supplierPaymentMethodId"
          >
            <Select
              allowClear
              placeholder={t("Common-PaymentMethod")}
              showSearch
              optionFilterProp="children"
              filterOption={(input: string, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {getOptions(supplier?.paymentMethods)}
            </Select>
          </Form.Item>
          <Form.Item
            className={styles.articleInfoItem}
            name="deliveryTerm"
            label={t("NewSupplier-DeliveryTerm")}
            rules={[
              {
                required: true,
                message: t("Common-DeliveryTerm"),
              },
            ]}
          >
            <Input placeholder={t("NewSupplier-DeliveryTerm")} />
          </Form.Item>
          <Form.Item
            className={styles.articleInfoItem}
            name="minimumOrder"
            label={t("NewSupplier-MinimumOrder")}
            rules={[
              {
                required: true,
                message: t("NewSupplier-MinimumOrder"),
              },
            ]}
          >
            <Input placeholder={t("NewSupplier-MinimumOrder")} />
          </Form.Item>
          <Form.Item
            className={styles.articleInfoItem}
            name="purchaseUnit"
            label={t("NewSupplier-PurchaseUnit")}
          >
            <Select
              allowClear
              placeholder={t("Common-SearchByName")}
              showSearch
              onSearch={handleSearchPurchaseUnits}
              optionFilterProp="children"
              filterOption={(input: string, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {getOptionsPurchaseUnit(purchaseUnits)}
            </Select>
          </Form.Item>
          <Form.Item
            label={t("Common-Cost")}
            className={styles.articleInfoItem}
            name={"cost"}
            rules={[
              {
                required: true,
                message: t("Common-Cost"),
              },
            ]}
          >
            <Input
              className={styles.articleInfoItem}
              placeholder={t("Common-Cost")}
            />
          </Form.Item>
          <Form.Item
            label={t("Common-AgreedDiscounts")}
            className={styles.articleInfoItem}
            name={"agreedDiscounts"}
            rules={[
              {
                required: true,
                message: t("Common-AgreedDiscounts"),
              },
            ]}
          >
            <Input
              className={styles.articleInfoItem}
              placeholder={t("Common-AgreedDiscounts")}
            />
          </Form.Item>
          <Form.Item
            initialValue={false}
            label={t("NewSupplier-Principal")}
            name="principal"
          >
            <Switch />
          </Form.Item>
        </Form>
        <Button
          className={styles.addArticleButton}
          type="primary"
          onClick={handleAddArticle}
        >
          {t("Common-Add")}
        </Button>
      </Modal>
    </div>
  );
};

export default ViewSupplier;
